import { get, post, put, del } from '../../../../GlobalModules/Utility/Http';

/**
 * Get list of measurement units with filters
 * @param {Object} filters - Filters for the API
 * @returns {Promise<Object>} - Processed response with measurement units list
 */
export const getMeasurementUnitList = async (filters = {}) => {
  try {
    // Format filters according to the API structure
    let queryParams = {};
    
    if (filters) {
      // Handle search term
      if (filters.search) {
        queryParams.filter_op = JSON.stringify({ search_attr: "substring" });
        queryParams.filters = JSON.stringify({
          limit: filters.limit || 10,
          offset: filters.offset || 0,
          search_attr: filters.search
        });
      } else {
        // Handle pagination and sorting
        const filtersObj = {
          limit: filters.limit || 10,
          offset: filters.offset || 0
        };
        
        // Add sorting if provided
        if (filters.sort && filters.sort.length > 0) {
          filtersObj.sort = filters.sort.map(item => ({
            colId: item.field,
            sort: item.sort
          }));
        }
        queryParams.filters = JSON.stringify(filtersObj);
      }
    }
    
    const response = await get(`${process.env.EXTENDED_URL}/measurement_unit`, queryParams);
    // Ensure we're getting the total count from the response
    let processedResponse = measurementUnitListGet(response);
    
    // If the API response has a total field but our processed response doesn't have total_count
    if (response?.data?.total && !processedResponse.total) {
      processedResponse.total = response.data.total;
    }
    
    return processedResponse;
  } catch (error) {
    console.error('Error fetching measurement units list:', error);
    return {
      success: false,
      message: 'An error occurred while fetching measurement units',
      data: []
    };
  }
};

/**
 * Get details of a specific measurement unit
 * @param {string} slack - Measurement unit identifier
 * @returns {Promise<Object>} - Processed response with measurement unit details
 */
export const getMeasurementUnitDetails = async (slack) => {
  try {
    const response = await get(`${process.env.EXTENDED_URL}/measurement_unit/${slack}`);
    
    return measurementUnitDetailsGet(response);
  } catch (error) {
    console.error('Error fetching measurement unit details:', error);
    return {
      success: false,
      message: 'An error occurred while fetching measurement unit details',
      data: {}
    };
  }
};

/**
 * Create a new measurement unit
 * @param {Object} measurementUnitData - Data for creating measurement unit
 * @returns {Promise<Object>} - Processed response after creation attempt
 */
export const createMeasurementUnit = async (measurementUnitData) => {
  console.log("measurementUnitData", measurementUnitData);
  try {
    const apiData = {
      label: measurementUnitData.label,
      unit_code: measurementUnitData.unit_code,
      status: measurementUnitData.status || 1,
    };
console.log("apiData", apiData);
    const response = await post(`${process.env.EXTENDED_URL}/measurement_unit`, apiData);
    return measurementUnitAddPost(response);
  } catch (error) {
    console.error('Error creating measurement unit:', error);
    return {
      success: false,
      message: 'An error occurred while creating the measurement unit',
      data: {}
    };
  }
};

/**
 * Update an existing measurement unit
 * @param {string} slack - Measurement unit identifier
 * @param {Object} measurementUnitData - Data for updating measurement unit
 * @returns {Promise<Object>} - Processed response after update attempt
 */
export const updateMeasurementUnit = async (slack, measurementUnitData) => {
  try {
    const apiData = {
      label: measurementUnitData.label,
      unit_code: measurementUnitData.unit_code,
      status: measurementUnitData.status || 1
    };

    const response = await put(`${process.env.EXTENDED_URL}/measurement_unit/${slack}`, apiData);
    return measurementUnitUpdatePut(response);
  } catch (error) {
    console.error('Error updating measurement unit:', error);
    return {
      success: false,
      message: 'An error occurred while updating the measurement unit',
      data: {}
    };
  }
};

/**
 * Delete a measurement unit
 * @param {string} slack - Measurement unit identifier
 * @returns {Promise<Object>} - Processed response after deletion attempt
 */
export const deleteMeasurementUnit = async (slack) => {
  try {
    const response = await del(`${process.env.EXTENDED_URL}/measurement_unit/${slack}`);
    
    return measurementUnitDeleteDel(response);
  } catch (error) {
    console.error('Error deleting measurement unit:', error);
    return {
      success: false,
      message: 'An error occurred while deleting the measurement unit',
      data: {}
    };
  }
};

/**
 * Get measurement units for dropdown
 * @returns {Promise<Object>} - Processed response with measurement units list
 */
export const getMeasurementUnitsDropdown = async () => {
  try {
    const queryParams = {};

    const response = await get(`${process.env.EXTENDED_URL}/measurement_unit`, queryParams);
    
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        data: response.data.data || [],
        message: 'Measurement units retrieved successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to retrieve measurement units',
      data: []
    };
  } catch (error) {
    console.error('Error fetching measurement units dropdown:', error);
    return {
      success: false,
      message: 'An error occurred while fetching measurement units',
      data: []
    };
  }
};

/**
 * Process measurement unit list API response
 * @param {Object} response - API response
 * @returns {Object} - Processed response
 */
const measurementUnitListGet = (response) => {
  try {
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        total: response.data.total || 0,
        data: response.data.data || [],
        message: response.data.message || 'Measurement units retrieved successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to retrieve measurement units',
      total: 0,
      data: []
    };
  } catch (error) {
    console.error('Error processing measurement unit list response:', error);
    return {
      success: false,
      message: 'Error processing measurement unit list response',
      total: 0,
      data: []
    };
  }
};

/**
 * Process measurement unit details API response
 * @param {Object} response - API response
 * @returns {Object} - Processed response
 */
const measurementUnitDetailsGet = (response) => {
  try {
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        data: response.data.data || {},
        message: response.data.message || 'Measurement unit details retrieved successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to retrieve measurement unit details',
      data: {}
    };
  } catch (error) {
    console.error('Error processing measurement unit details response:', error);
    return {
      success: false,
      message: 'Error processing measurement unit details response',
      data: {}
    };
  }
};

/**
 * Process measurement unit add API response
 * @param {Object} response - API response
 * @returns {Object} - Processed response
 */
const measurementUnitAddPost = (response) => {
  try {
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        data: response.data.data || {},
        message: response.data.message || 'Measurement unit added successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to add measurement unit',
      data: {}
    };
  } catch (error) {
    console.error('Error processing measurement unit add response:', error);
    return {
      success: false,
      message: 'Error processing measurement unit add response',
      data: {}
    };
  }
};

/**
 * Process measurement unit update API response
 * @param {Object} response - API response
 * @returns {Object} - Processed response
 */
const measurementUnitUpdatePut = (response) => {
  try {
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        data: response.data.data || {},
        message: response.data.message || 'Measurement unit updated successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to update measurement unit',
      data: {}
    };
  } catch (error) {
    console.error('Error processing measurement unit update response:', error);
    return {
      success: false,
      message: 'Error processing measurement unit update response',
      data: {}
    };
  }
};

/**
 * Process measurement unit delete API response
 * @param {Object} response - API response
 * @returns {Object} - Processed response
 */
const measurementUnitDeleteDel = (response) => {
  try {
    if (response && response.data && response.data.success === true) {
      return {
        success: true,
        data: response.data.data || {},
        message: response.data.message || 'Measurement unit deleted successfully'
      };
    }
    return {
      success: false,
      message: (response && response.data && response.data.message) || 'Failed to delete measurement unit',
      data: {}
    };
  } catch (error) {
    console.error('Error processing measurement unit delete response:', error);
    return {
      success: false,
      message: 'Error processing measurement unit delete response',
      data: {}
    };
  }
};
